import { Injectable } from '@angular/core';
import { FormArray } from '@angular/forms';
import { map } from 'rxjs/operators';
import { ModalService } from '@purespectrum1/ui/modal';

import { notifyMessage } from '../../shared/constants/notify-message';
import { Constants } from '../../shared/constants/create-survey.constants';
import { QUALIFICATION_CODES } from '../../shared/constants/qualification-code';
import { Flexible } from '../../shared/interfaces/create-surveys.interface';
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import {
  BuyerSurveyResponse,
  Quotas,
  Supplier,
} from '../../shared/interfaces/survey.interface';
import { Qualification } from '../../shared/interfaces/qualification.interface';
import {
  NestedQuota,
  SelectedQuota,
} from '../../shared/interfaces/quota.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { Child, Children } from 'purespectrum-lib';
import { QUALIFICATIONS_CATEGORIES } from '../../shared/constants/qualification-categories';
import { CommonService } from '../../shared/services/common-service/common-service';
import { SurveyNestedQuotas } from '../domain/survey-nested-quotas';
import { NestedAllocation } from '../quota-nesting/domain/nested-allocation';

@Injectable({
  providedIn: 'root',
})
export class QuotaModalService {
  constructor(
    private _toastrService: ToasterService,
    private _modal: ModalService,
    private _commonService: CommonService
  ) {}

  setCensusFlag(qualification: Qualification) {
    return qualification.census?.enabled && qualification.census?.data?.length
      ? true
      : false;
  }

  inValidComplete(complete: number) {
    if (!complete) {
      this._toastrService.warning(
        notifyMessage.warningMessage.QUOTA_MODAL.MISSING_NO_OF_COMPLETE
      );
      return true;
    }
    return false;
  }

  isValidAllocation(
    complete: number,
    control: FormArray,
    index: number,
    validatePercentage: boolean = true
  ) {
    const allocatedCompletes = control.controls.reduce((previous, current) => {
      return previous + Number(current.value.number);
    }, 0);

    const allocatedPercentage = control.controls.reduce((previous, current) => {
      return previous + Number(current.value.percentage);
    }, 0);

    if (
      this._getValidationCondition(
        complete,
        allocatedCompletes,
        allocatedPercentage,
        validatePercentage
      )
    ) {
      control.controls[index].patchValue({
        number: '',
        percentage: '',
        minimum: '',
        maximum: '',
      });
      this._toastrService.warning(
        notifyMessage.warningMessage.QUOTA_MODAL.INVALID_QUOTA_COMPLETE
      );
      return false;
    }

    return true;
  }

  private _getValidationCondition(
    completes: number,
    allocatedCompletes: number,
    allocatedPercentage: number,
    validatePercentage: boolean
  ) {
    return validatePercentage
      ? completes - allocatedCompletes < 0 || allocatedPercentage > 100
      : completes - allocatedCompletes < 0;
  }

  getNumber(
    complete: number,
    quotas: SelectedQuota[],
    value: number,
    index: number,
    isCensusApplied: boolean = false
  ) {
    let number = Math.round((value * Number(complete)) / 100);
    const quotaCompletes = quotas.reduce((previous, current, currentIndex) => {
      if (index !== currentIndex) {
        return previous + Number(current.number);
      }
      return previous;
    }, 0);
    const remaining = complete - quotaCompletes;
    if (number && remaining < number) {
      number -= number - remaining;
    } else if (isCensusApplied && number && remaining > number) {
      number += remaining - number;
    }

    return number;
  }

  getFlexibleValue(
    value: number,
    flexible: Flexible = { value: 0, enable: true }
  ) {
    if (flexible.enable) {
      const percentage = Math.round((Number(value) * flexible.value) / 100);
      return [Number(value) - percentage, Number(value) + percentage];
    }

    return [Number(value), Number(value)];
  }

  distributeQuotas(
    qualification: Qualification,
    complete: number,
    flexible: Flexible = { value: 0, enable: true }
  ) {
    if (qualification.census?.data?.length) {
      return qualification.census.data.map((census) => {
        let [minimum, maximum] = this.getFlexibleValue(
          census.rep_value,
          flexible
        );
        return {
          selected: true,
          text: census.name,
          number: Math.round((census.rep_value * Number(complete)) / 100),
          percentage: Number(census.rep_value),
          minimum: Number(minimum),
          maximum: Number(maximum),
        };
      });
    }
    return [];
  }

  redistributeQuotasForSpvSurvey(
    survey: BuyerSurveyResponse,
    isClickBalanceEnabled: boolean
  ) {
    const completes = isClickBalanceEnabled
      ? this._commonService.calculateClicks(
          survey.completes_required,
          survey.expected_ir
        )
      : survey.completes_required;

    const distributedQuotas: Quotas[] = [];
    survey.qualifications.forEach((q) => {
      const quotas = survey.quotas.filter(
        (qt) =>
          qt.criteria?.some(
            (c) => c.qualification_code === q.qualification_code
          ) && qt.quota_category !== QUALIFICATIONS_CATEGORIES.NESTED
      );
      const distributedQuota = this.reDistributeQuotaQuantites(
        quotas as unknown as SelectedQuota[],
        completes
      );
      distributedQuotas.push(...this.mapToQuotaPayload(distributedQuota));
    });

    const nestedQutoas = survey.quotas.filter(
      (q) => q.quota_category === QUALIFICATIONS_CATEGORIES.NESTED
    );
    let distributedNestedQuotas: NestedQuota[] = [];
    if (nestedQutoas.length) {
      distributedNestedQuotas = this.nestedQuotasRedistribution(
        nestedQutoas,
        completes
      );
    }
    survey.quotas = [
      ...distributedNestedQuotas,
      ...distributedQuotas,
    ] as Quotas[];
    return survey;
  }

  public nestedQuotasRedistribution(
    nestedQuotas: NestedQuota[],
    totalCompletesOrClicks: number
  ) {
    const surveyNestedQuotas = new SurveyNestedQuotas(nestedQuotas);
    return surveyNestedQuotas
      .groupIds()
      .map((groupId) =>
        this._redistributeNestedQuotaForEachNestingGroup(
          surveyNestedQuotas.kind(groupId).get(),
          totalCompletesOrClicks
        )
      )
      .flat();
  }

  private _redistributeNestedQuotaForEachNestingGroup(
    nestedQuotas: NestedQuota[],
    totalCompletesOrClicks: number
  ) {
    nestedQuotas.forEach((nestedQuota: NestedQuota) => {
      let number = Math.round(
        (Number(nestedQuota.percentage) * totalCompletesOrClicks) / 100
      );
      nestedQuota = this._setQuotaQuantity(nestedQuota, number);

      nestedQuota?.individual_quotas?.map((quota) => {
        const allocation = Math.round(
          (quota?.allocation?.percentage * totalCompletesOrClicks) / 100
        );
        quota.allocation.quantity = allocation;
        quota.allocation.min = allocation;
        quota.allocation.max = allocation;
      });
    });
    return new NestedAllocation(totalCompletesOrClicks, nestedQuotas).audit();
  }

  private _setQuotaQuantity(quota: NestedQuota, quantity: number) {
    quota.current_target = quantity;
    quota.required_count = quantity;
    quota.number = quantity;
    quota.minimum = quantity;
    quota.maximum = quantity;
    quota.min = quantity;
    quota.max = quantity;
    return quota;
  }

  mapToQuotaPayload(quotas: SelectedQuota[]): Quotas[] {
    const mappedQutoas: Quotas[] = [];
    quotas.forEach((q) => {
      const { number, suppliers, ...quotaPayload } = q as any;
      quotaPayload.required_count = number;
      mappedQutoas.push(quotaPayload);
    });
    return mappedQutoas;
  }

  reDistributeQuotaQuantites(
    quotas: SelectedQuota[],
    complete: number,
    flexible: Flexible = {
      enable: true,
      value: 0,
    }
  ) {
    if (!quotas.length) {
      return quotas;
    }
    const updatedQuotas = quotas.map((quota, index) => {
      let number = Math.round((quota.percentage * Number(complete)) / 100);
      let [minimum, maximum] = this.getFlexibleValue(number, flexible);
      quota.suppliers = this._redistributeQBSCurrentTarget(
        quota.number,
        number,
        quota.suppliers
      );
      quota.number = number;
      quota.maximum = maximum;
      quota.minimum = minimum;
      return quota;
    });
    // distribute remaining
    const quotasCompletes = updatedQuotas.reduce((acc, quota) => {
      return acc + quota.number;
    }, 0);
    if (quotasCompletes !== complete && updatedQuotas.length) {
      let remaining = complete - quotasCompletes;
      let n = remaining > 0 ? 1 : -1;
      let i = 0; // started with first row(0 index)
      while (remaining !== 0) {
        if (!updatedQuotas[i]) {
          i = 0;
        }
        let isUpdated = false;
        if (updatedQuotas[i] && updatedQuotas[i].number) {
          updatedQuotas[i].suppliers = this._redistributeQBSCurrentTarget(
            updatedQuotas[i].number,
            updatedQuotas[i].number + n,
            updatedQuotas[i].suppliers
          );
          updatedQuotas[i].number = updatedQuotas[i].number + n;
          let [minimum, maximum] = this.getFlexibleValue(
            updatedQuotas[i].number,
            flexible
          );
          updatedQuotas[i].minimum = minimum;
          updatedQuotas[i].maximum = maximum;
          isUpdated = true;
        }

        if (isUpdated) {
          if (n > 0) {
            remaining--;
          } else {
            remaining++;
          }
        }

        i++;
      }
    }
    return updatedQuotas;
  }

  private _redistributeQBSCurrentTarget(
    oldQuotaCT: number,
    newQuotaCT: number,
    suppliers: Supplier[] = []
  ) {
    // we are redistributing QBS supplier current_target so that pro-rata modal does not open always on changing completes
    return suppliers.map((supplier) => {
      supplier.current_target = oldQuotaCT
        ? Math.round((supplier.current_target / oldQuotaCT) * newQuotaCT)
        : newQuotaCT;
      return supplier;
    });
  }

  adjustQuotaQuantites(
    distributedQuotas: SelectedQuota[],
    complete: number,
    flexible: Flexible = { value: 0, enable: true },
    isCensusApplied: boolean = false
  ) {
    if (distributedQuotas.length) {
      return distributedQuotas.map((quota, index) => {
        const number = this.getNumber(
          complete,
          distributedQuotas,
          quota.percentage,
          index,
          isCensusApplied
        );
        let [minimum, maximum] = this.getFlexibleValue(number, flexible);
        let obj = {
          selected: !!number,
          text: quota.text,
          number: number,
          percentage: quota.percentage,
          minimum: minimum,
          maximum: maximum,
        };
        distributedQuotas[index] = obj;
        return obj;
      });
    }

    return [];
  }

  private _openModifySelectionConfirmationModal() {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: 'Modifying the qualifications will delete the current quotas',
      },
    });
    return modalRef.onClose$.pipe(map((msg) => msg === 'ok')).toPromise();
  }

  async quotaConfirmationChecked(
    quotas: SelectedQuota[] | undefined,
    hasNestedQuotas: boolean = false
  ) {
    let deleteQuotaOnQualificationChange = false;
    let isProceed = true;
    if ((quotas && quotas?.length > 0) || hasNestedQuotas) {
      const isOk = await this._openModifySelectionConfirmationModal();
      if (!isOk) {
        isProceed = false;
      } else {
        deleteQuotaOnQualificationChange = true;
      }
    }
    return [isProceed, deleteQuotaOnQualificationChange];
  }

  validateMinMaxAge(control: FormArray, i: number) {
    let result: boolean = true;
    if (this._validateMonthYearUnit(control, i)) {
      this._toastrService.warning(
        notifyMessage.errorMessage.QUOTA_MODAL.INVALID_RANGE_YEAR_MONTH
      );
      return false;
    }
    if (
      parseInt(control.controls[i].value.min) >
      parseInt(control.controls[i].value.max)
    ) {
      this._toastrService.warning(
        notifyMessage.warningMessage.QUOTA_MODAL.INVALID_RANGE
      );
      return false;
    }
    control.controls.forEach((values, index) => {
      if (
        index != 0 &&
        (control.controls[0].value.gender ==
          control.controls[index].value.gender ||
          control.controls[0].value.gender == 3 ||
          control.controls[index].value.gender == 3)
      ) {
        if (
          !(
            (parseInt(control.controls[0].value.min) <
              parseInt(control.controls[index].value.min) ||
              parseInt(control.controls[0].value.min) >
                parseInt(control.controls[index].value.max)) &&
            (parseInt(control.controls[0].value.max) <
              parseInt(control.controls[index].value.min) ||
              parseInt(control.controls[0].value.max) >
                parseInt(control.controls[index].value.max))
          )
        ) {
          this._toastrService.warning(
            notifyMessage.warningMessage.QUOTA_MODAL.INVALID_RANGE
          );
          result = false;
        }
      }
    });
    return result;
  }

  _validateMonthYearUnit(control: FormArray, index: number) {
    const maxValue =
      control.controls[index].value.ageunit === Constants.UNITS.AGE.YEAR
        ? Constants.UNITS.LIMIT_AGE_CHILD.LIMIT_YEAR
        : Constants.UNITS.LIMIT_AGE_CHILD.LIMIT_MONTH;
    if (
      parseInt(control.controls[index].value.max) > maxValue ||
      parseInt(control.controls[index].value.max) < 1 ||
      parseInt(control.controls[index].value.min) > maxValue ||
      parseInt(control.controls[index].value.min) < 0
    ) {
      return true;
    }
    return false;
  }

  distributeEquallyPercentage(
    quotas: SelectedQuota[],
    qualification: Qualification,
    completesRequired: number
  ) {
    let totalCompletes = 0;
    let firstQuotaSkip = -1;
    let quotaLength = 0;
    const haveChildrenStatus = this._findHaveChildrenStatus(qualification);
    if (
      [
        QUALIFICATION_CODES.CHILDREN,
        QUALIFICATION_CODES.CHILD_AGE_GENDER,
      ].includes(qualification.qualification_code) ||
      qualification.is_range
    ) {
      firstQuotaSkip = 0;
      if (haveChildrenStatus && quotas.length > 1) {
        quotas = this._checkQualificationForChildren(quotas, qualification);
        quotaLength = 1;
      }
    }
    let completesNeeded = Number(completesRequired) - Number(totalCompletes);
    let complete = 1;
    if (quotas && quotas.length > quotaLength) {
      while (completesNeeded > 0) {
        quotas.forEach((quota, index) => {
          if (completesNeeded > 0 && index > firstQuotaSkip) {
            quota.number = quota.number + complete;
            completesNeeded = completesNeeded - complete;
            totalCompletes = totalCompletes + complete;
          }
        });
      }
    }
    return { quotas, totalCompletes, completesNeeded };
  }

  private _checkQualificationForChildren(
    quotas: SelectedQuota[],
    qualification: Qualification
  ) {
    if (qualification.data) {
      qualification.data.forEach((qualificationData: any) => {
        if (
          qualificationData.hasOwnProperty('selected') &&
          !qualificationData.selected
        ) {
          quotas = quotas.filter(
            (quota) => quota.text !== qualificationData.text
          );
        }
      });
    }
    return quotas;
  }

  private _findHaveChildrenStatus(qualification: Qualification) {
    if (
      [
        QUALIFICATION_CODES.CHILDREN,
        QUALIFICATION_CODES.CHILD_AGE_GENDER,
      ].includes(qualification.qualification_code)
    ) {
      return qualification.data.some(
        (qualificationData: any) =>
          qualificationData.id == Constants.CHILDREN.HAVE_CHILDREN &&
          qualificationData.selected
      );
    }
    return false;
  }

  public mapChildAgeGender(quotas: SelectedQuota[]): SelectedQuota[] {
    return quotas.map((quota): SelectedQuota => {
      let conditionCodes = quota.options;
      let ageunit = quota.ageunit;
      if (quota.criteria && quota.criteria[0]) {
        if (quota.criteria[0].condition_codes && !quota.options) {
          conditionCodes = quota.criteria[0].condition_codes;
        }
        if (quota.criteria[0].child_age_unit) {
          ageunit = quota.criteria[0].child_age_unit;
        }
      }
      if (!conditionCodes) {
        return quota;
      }
      const childrenQuota = conditionCodes.map(
        (option: any) => new Child(Number(option))
      );
      const children = new Children(childrenQuota, ageunit);
      if (children.noChildren) {
        return {
          ...quota,
          text: children.description,
          ageunit: ageunit,
        };
      }
      return {
        ...quota,
        number: quota.current_target || quota.number,
        gender: children.gender.toString(),
        ageunit: ageunit,
        min: children.age.from,
        max: children.age.to,
        text: children.description,
      };
    });
  }
}
